<div class="stock-home">
  <div class="stock-home__header">
    <div class="stock-home__heading">
      <h1 class="page-title">Stock Overview</h1>
      <p class="page-subtitle">Browse inventory by product first, then drill into warehouse-by-warehouse availability.</p>
    </div>
    <app-button type="button" (click)="goToAdjustment()">Adjust Stock</app-button>
  </div>

  <app-card>
    <div class="stock-home__toolbar">
      <label class="stock-home__search-field">
        <ng-glyph class="stock-home__search-icon" name="search" size="20"></ng-glyph>
        <input
          class="stock-home__search-input"
          type="text"
          [value]="searchTerm"
          placeholder="Search product, SKU, or warehouse..."
          (input)="onSearchInput($any($event.target).value)" />
      </label>

      <div class="stock-home__filters">
        <label class="stock-home__select-group">
          <span class="stock-home__select-label">Warehouse</span>
          <div class="stock-home__select-wrap">
            <select class="stock-home__select" [ngModel]="selectedWarehouseId" (ngModelChange)="onWarehouseChange($event)">
              <option value="">All Warehouses</option>
              @for (warehouse of warehouses; track warehouse.id) {
              <option [value]="warehouse.id">{{ warehouse.name }}</option>
              }
            </select>
            <ng-glyph class="stock-home__select-caret" name="expand_more" size="18"></ng-glyph>
          </div>
        </label>

        <label class="stock-home__select-group">
          <span class="stock-home__select-label">Products per page</span>
          <div class="stock-home__select-wrap">
            <select
              class="stock-home__select"
              [ngModel]="stockState.pagination().limit"
              (ngModelChange)="onPageSizeChange($event.toString())">
              @for (size of pageSizeOptions; track size) {
              <option [value]="size">{{ size }}</option>
              }
            </select>
            <ng-glyph class="stock-home__select-caret" name="expand_more" size="18"></ng-glyph>
          </div>
        </label>
      </div>
    </div>
  </app-card>

  <app-card>
    <app-summary-panel
      title="Inventory Summary"
      subtitle="Keep the current warehouse scope and total stock picture visible while browsing products."
      [rows]="summaryRows"></app-summary-panel>
  </app-card>

  @if (stockState.error() && !stockState.loading()) {
  <app-card>
    <div class="stock-home__state stock-home__state--error">
      <ng-glyph name="error" size="22"></ng-glyph>
      <span>{{ stockState.error() }}</span>
    </div>
  </app-card>
  } @if (stockState.loading() && stockState.data().length === 0) {
  <app-card>
    <div class="stock-home__state">
      <ng-glyph name="refresh" size="24" class="stock-home__spin"></ng-glyph>
      <span>Loading product inventory...</span>
    </div>
  </app-card>
  } @else if (!stockState.loading() && stockState.data().length === 0) {
  <app-card>
    <div class="stock-home__state">
      <ng-glyph name="inventory_2" size="24"></ng-glyph>
      <span>No stock summary matches the current filters.</span>
    </div>
  </app-card>
  } @else {
  <div class="stock-home__list">
    @for (product of stockState.data(); track product.materialId) {
    <app-card>
      <article class="stock-home__product-card">
        <div class="stock-home__product-header">
          <div class="stock-home__product-heading">
            <div class="stock-home__product-title-row">
              <h2 class="stock-home__product-title">{{ product.material.name }}</h2>
              <span
                class="stock-home__availability-pill"
                [class.stock-home__availability-pill--healthy]="getAvailabilityTone(product.available) === 'healthy'"
                [class.stock-home__availability-pill--warning]="getAvailabilityTone(product.available) === 'warning'"
                [class.stock-home__availability-pill--critical]="getAvailabilityTone(product.available) === 'critical'">
                {{ product.available > 0 ? "Available" : "Out of Stock" }}
              </span>
            </div>
            <p class="stock-home__product-subtitle">{{ product.material.sku }} · {{ selectedWarehouseName }}</p>
          </div>

          <button class="stock-home__expand-btn" type="button" (click)="toggleExpanded(product.materialId)">
            <span>{{ isExpanded(product.materialId) ? "Hide Warehouses" : "Show Warehouses" }}</span>
            <ng-glyph [name]="isExpanded(product.materialId) ? 'expand_less' : 'expand_more'" size="18"></ng-glyph>
          </button>
        </div>

        <div class="stock-home__metrics">
          <div class="stock-home__metric stock-home__metric--on-hand">
            <div class="stock-home__metric-top">
              <ng-glyph class="stock-home__metric-icon" name="inventory_2" size="18"></ng-glyph>
              <span class="stock-home__metric-label">On Hand</span>
            </div>
            <strong class="stock-home__metric-value">{{ formatQuantity(product.onHand) }}</strong>
          </div>
          <div class="stock-home__metric stock-home__metric--reserved">
            <div class="stock-home__metric-top">
              <ng-glyph class="stock-home__metric-icon" name="lock" size="18"></ng-glyph>
              <span class="stock-home__metric-label">Reserved</span>
            </div>
            <strong class="stock-home__metric-value">{{ formatQuantity(product.reserved) }}</strong>
          </div>
          <div class="stock-home__metric stock-home__metric--available">
            <div class="stock-home__metric-top">
              <ng-glyph class="stock-home__metric-icon" name="verified" size="18"></ng-glyph>
              <span class="stock-home__metric-label">Available</span>
            </div>
            <strong class="stock-home__metric-value">{{ formatQuantity(product.available) }}</strong>
          </div>
        </div>

        <div class="stock-home__product-meta">
          <span>{{ product.warehouseCount }} {{ product.warehouseCount === 1 ? "warehouse" : "warehouses" }}</span>
          <span>Updated {{ formatUpdatedAt(product.updatedAt) }}</span>
        </div>

        @if (isExpanded(product.materialId)) {
        <div class="stock-home__breakdown">
          <div class="stock-home__breakdown-header">
            <span>Warehouse</span>
            <span>On Hand</span>
            <span>Reserved</span>
            <span>Available</span>
            <span></span>
          </div>

          @for (warehouse of product.warehouses; track warehouse.warehouseId) {
          <div class="stock-home__breakdown-row">
            <span class="stock-home__breakdown-name">{{ warehouse.warehouse.name }}</span>
            <span>{{ formatQuantity(warehouse.onHand) }}</span>
            <span>{{ formatQuantity(warehouse.reserved) }}</span>
            <span>{{ formatQuantity(warehouse.available) }}</span>
            <button class="stock-home__breakdown-action" type="button" (click)="openStockDetail(warehouse.stockId)">
              View Movements
            </button>
          </div>
          }
        </div>
        }
      </article>
    </app-card>
    }
  </div>

  <app-card>
    <div class="stock-home__pagination">
      <div class="stock-home__pagination-info">
        Showing {{ getPaginationStart() }} to {{ getPaginationEnd() }} of {{ stockState.pagination().total }} products
      </div>

      <div class="stock-home__pagination-controls">
        <button
          class="stock-home__pagination-btn"
          type="button"
          [disabled]="stockState.pagination().page <= 1"
          (click)="onPageChange(stockState.pagination().page - 1)">
          <ng-glyph name="chevron_left" size="18"></ng-glyph>
        </button>

        @for (page of getPaginationRange(); track page) {
        <button
          class="stock-home__pagination-btn"
          type="button"
          [class.stock-home__pagination-btn--active]="page === stockState.pagination().page"
          [disabled]="page === stockState.pagination().page"
          (click)="onPageChange(page)">
          {{ page }}
        </button>
        }

        <button
          class="stock-home__pagination-btn"
          type="button"
          [disabled]="stockState.pagination().page >= stockState.pagination().pages"
          (click)="onPageChange(stockState.pagination().page + 1)">
          <ng-glyph name="chevron_right" size="18"></ng-glyph>
        </button>
      </div>
    </div>
  </app-card>
  }
</div>
